import { apiSlice } from "../../../_api/apiSlice";
import { normalizeStageCatalog } from "../utils/stageStyles";
import { normalizeMoneyPresets } from "../utils/moneyFormat";
import { normalizeItemsTableCatalog } from "../utils/itemsTableCatalog";

const BASE = "/document-templates";
const CATALOG_CACHE_SEC = 3600;

const templateType = (type) => {
  if (type === "proposal") return "proposal";
  if (type === "upsell") return "upsell";
  return "invoice";
};

const normalizeListResponse = (response) => {
  if (Array.isArray(response)) {
    return { data: response, total: response.length };
  }

  const data =
    response?.data ||
    response?.templates ||
    response?.items ||
    response?.result ||
    [];

  const pagination = response?.pagination || {};

  return {
    data: Array.isArray(data) ? data : [],
    total:
      pagination.total ||
      response?.total ||
      response?.count ||
      data?.length ||
      0,
    page: pagination.page || response?.page || 1,
    limit: pagination.limit || response?.limit || 10,
  };
};

const normalizeItem = (response) =>
  response?.data || response?.template || response;

const itemTags = (result, _error, id) => [
  { type: "DocumentTemplate", id: "LIST" },
  { type: "DocumentTemplate", id: result?._id || result?.id || id },
];

const listTag = [{ type: "DocumentTemplate", id: "LIST" }];

export const documentTemplateApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // GET /api/document-templates
    getTemplates: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        type = "",
        status = "",
        sortBy = "",
        sortOrder = "",
      } = {}) => ({
        url: BASE,
        method: "GET",
        params: {
          ...(page && { page }),
          ...(limit && { limit }),
          ...(search && { search }),
          ...(type && { type }),
          ...(status && { status }),
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) => [
        { type: "DocumentTemplate", id: "LIST" },
        ...(result?.data || []).map(({ _id, id }) => ({
          type: "DocumentTemplate",
          id: _id || id,
        })),
      ],
    }),

    // GET /api/document-templates/stage-catalog?type=invoice|proposal
    // Cached per type; stage switching in the editor never refetches it.
    getStageCatalog: builder.query({
      query: (type = "invoice") => ({
        url: `${BASE}/stage-catalog`,
        method: "GET",
        params: { type: templateType(type) },
      }),
      transformResponse: (response, _meta, arg) =>
        normalizeStageCatalog(response?.data ?? response, templateType(arg)),
      keepUnusedDataFor: CATALOG_CACHE_SEC,
    }),

    // GET /api/document-templates/money-format-presets
    // Spacing presets for the {{money}} helper. One list for every template,
    // so it is fetched once and cached for the session.
    getMoneyFormatPresets: builder.query({
      query: () => ({
        url: `${BASE}/money-format-presets`,
        method: "GET",
      }),
      transformResponse: (response) =>
        normalizeMoneyPresets(response?.data ?? response),
      keepUnusedDataFor: CATALOG_CACHE_SEC,
    }),

    // GET /api/document-templates/items-table-catalog?type=invoice|proposal
    getItemsTableCatalog: builder.query({
      query: (type = "invoice") => ({
        url: `${BASE}/items-table-catalog`,
        method: "GET",
        params: { type: templateType(type) },
      }),
      transformResponse: (response, _meta, arg) =>
        normalizeItemsTableCatalog(
          response?.data ?? response,
          templateType(arg),
        ),
      keepUnusedDataFor: CATALOG_CACHE_SEC,
    }),

    // Fetched once per document type; stage is applied client-side in the editor.
    getTemplateSampleData: builder.query({
      query: (arg = "invoice") => {
        const type = typeof arg === "string" ? arg : arg?.type || "invoice";

        return {
          url: `${BASE}/sample-data`,
          method: "GET",
          params: { type: templateType(type) },
        };
      },
      transformResponse: (response) => {
        // Backend: { success, data: { type, data: sample } }
        const payload = response?.data || response;
        if (payload?.data?.company) return payload.data;
        if (payload?.company) return payload;
        return payload?.data || payload || {};
      },
      keepUnusedDataFor: CATALOG_CACHE_SEC,
    }),

    // POST /api/document-templates/ensure-defaults
    // Idempotent — seeds Default Invoice / Proposal / Upsell when any are missing.
    ensureTemplateDefaults: builder.mutation({
      query: () => ({
        url: `${BASE}/ensure-defaults`,
        method: "POST",
      }),
      transformResponse: (response) => response?.data || response || [],
      invalidatesTags: listTag,
    }),

    // GET /api/document-templates/:id
    getTemplateById: builder.query({
      query: (id) => ({ url: `${BASE}/${id}`, method: "GET" }),
      transformResponse: normalizeItem,
      providesTags: (result, _error, id) => [
        { type: "DocumentTemplate", id: result?._id || result?.id || id },
      ],
    }),

    // POST /api/document-templates
    createTemplate: builder.mutation({
      query: (payload) => ({ url: BASE, method: "POST", body: payload }),
      transformResponse: normalizeItem,
      invalidatesTags: listTag,
    }),

    // PUT /api/document-templates/:id  (auto-versions on backend)
    // Do NOT invalidate the current template id — that refetches and remounts the editor.
    updateTemplate: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `${BASE}/${id}`,
        method: "PUT",
        body: payload,
      }),
      transformResponse: normalizeItem,
      async onQueryStarted({ id, ...payload }, { dispatch, queryFulfilled }) {
        const html = payload?.html;
        if (
          html &&
          /drop blocks here/i.test(String(html).replace(/<[^>]+>/g, " "))
        ) {
          try {
            await queryFulfilled;
          } catch (_e) {
            // ignore
          }
          return;
        }
        const patch = dispatch(
          documentTemplateApi.util.updateQueryData(
            "getTemplateById",
            id,
            (draft) => {
              if (!draft || typeof draft !== "object") return;
              Object.assign(draft, payload);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch (_e) {
          patch.undo();
        }
      },
    }),

    // DELETE /api/document-templates/:id  (soft-delete)
    deleteTemplate: builder.mutation({
      query: (id) => ({ url: `${BASE}/${id}`, method: "DELETE" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "DocumentTemplate", id: "LIST" },
        { type: "DocumentTemplate", id },
      ],
    }),

    // POST /api/document-templates/:id/duplicate
    duplicateTemplate: builder.mutation({
      query: (id) => ({ url: `${BASE}/${id}/duplicate`, method: "POST" }),
      transformResponse: normalizeItem,
      invalidatesTags: listTag,
    }),

    // POST /api/document-templates/:id/restore
    restoreTemplate: builder.mutation({
      query: (id) => ({ url: `${BASE}/${id}/restore`, method: "POST" }),
      transformResponse: normalizeItem,
      invalidatesTags: (result, _error, id) => itemTags(result, _error, id),
    }),

    // POST /api/document-templates/:id/default
    setDefaultTemplate: builder.mutation({
      query: (id) => ({
        url: `${BASE}/${id}/default`,
        method: "POST",
      }),
      invalidatesTags: listTag,
    }),

    // POST /api/document-templates/:id/approve  { source: 'default' | 'customized' }
    approveTemplate: builder.mutation({
      query: ({ id, source = "default" }) => ({
        url: `${BASE}/${id}/approve`,
        method: "POST",
        body: { source },
      }),
      transformResponse: normalizeItem,
      invalidatesTags: (result, _error, arg) => itemTags(result, _error, arg.id),
    }),

    // POST /api/document-templates/:id/publish
    publishTemplate: builder.mutation({
      query: (id) => ({ url: `${BASE}/${id}/publish`, method: "POST" }),
      transformResponse: normalizeItem,
      invalidatesTags: (result, _error, id) => itemTags(result, _error, id),
    }),

    // POST /api/document-templates/:id/preview  — returns rendered HTML
    previewTemplate: builder.mutation({
      query: ({ id, data, stage }) => ({
        url: `${BASE}/${id}/preview`,
        method: "POST",
        body: { data, ...(stage ? { stage } : {}) },
      }),
      transformResponse: (response) =>
        response?.data || response?.html || response?.preview || response,
    }),

    // POST /api/document-templates/:id/render  — full render with payload
    renderTemplate: builder.mutation({
      query: ({ id, data, options, stage }) => ({
        url: `${BASE}/${id}/render`,
        method: "POST",
        body: { data, options, ...(stage ? { stage } : {}) },
      }),
      transformResponse: (response) =>
        response?.data || response?.html || response?.result || response,
    }),

    // POST /api/document-templates/:id/pdf  — streams PDF; return blob URL
    downloadTemplatePdf: builder.mutation({
      query: ({ id, data, pdfOptions, stage }) => ({
        url: `${BASE}/${id}/pdf`,
        method: "POST",
        body: { data, pdfOptions, ...(stage ? { stage } : {}) },
        responseHandler: async (response) => {
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        },
        cache: "no-cache",
      }),
    }),

    // POST /api/document-templates/:id/send-demo — email demo PDF.
    // `stages: "all"` asks the server for one demo per stage in the catalog.
    sendTemplateDemo: builder.mutation({
      query: ({ id, to, data, stage, stages } = {}) => ({
        url: `${BASE}/${id}/send-demo`,
        method: "POST",
        body: {
          ...(to ? { to } : {}),
          ...(data && Object.keys(data).length ? { data } : {}),
          ...(stage && !stages ? { stage } : {}),
          ...(stages ? { stages } : {}),
        },
      }),
      transformResponse: (response) => response?.data || response,
    }),

    // GET /api/document-templates/:id/versions
    // Default response is metadata-only; pass { id, includeContent: true } when html is needed.
    getTemplateVersions: builder.query({
      query: (arg) => {
        const id = typeof arg === "string" ? arg : arg?.id;
        const includeContent = typeof arg === "object" && arg?.includeContent;

        return {
          url: `${BASE}/${id}/versions`,
          method: "GET",
          ...(includeContent ? { params: { includeContent: "1" } } : {}),
        };
      },
      transformResponse: (response) => {
        const data = response?.data || response;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.history)) return data.history;
        if (Array.isArray(data?.versions)) return data.versions;
        return [];
      },
      providesTags: (_result, _error, arg) => [
        {
          type: "DocumentTemplateVersions",
          id: typeof arg === "string" ? arg : arg?.id,
        },
      ],
    }),

    // POST /api/document-templates/:id/rollback
    rollbackTemplate: builder.mutation({
      query: ({ id, version }) => ({
        url: `${BASE}/${id}/rollback`,
        method: "POST",
        body: { version },
      }),
      transformResponse: normalizeItem,
      invalidatesTags: (result, _error, arg) => [
        { type: "DocumentTemplate", id: "LIST" },
        { type: "DocumentTemplate", id: arg.id },
        { type: "DocumentTemplateVersions", id: arg.id },
      ],
    }),
  }),
});

export const {
  useGetTemplatesQuery,
  useEnsureTemplateDefaultsMutation,
  useGetTemplateByIdQuery,
  useGetTemplateSampleDataQuery,
  useGetStageCatalogQuery,
  useGetMoneyFormatPresetsQuery,
  useGetItemsTableCatalogQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useDuplicateTemplateMutation,
  useRestoreTemplateMutation,
  useSetDefaultTemplateMutation,
  usePublishTemplateMutation,
  useApproveTemplateMutation,
  usePreviewTemplateMutation,
  useRenderTemplateMutation,
  useDownloadTemplatePdfMutation,
  useSendTemplateDemoMutation,
  useGetTemplateVersionsQuery,
  useRollbackTemplateMutation,
} = documentTemplateApi;
